// sampler.ts — picks the question sequence for a session.
//
// PRD §5.1 — quiz is built around dimensions. Each dimension has a pool of
//   variants; one is sampled per session.
// PRD §5.2 — sequence length adapts to the user's stated cert (non-diver,
//   OW casual, AOW experienced).
// PRD §5.4 — branch dims activate based on prior answers (cert, vibe, days).
// PRD §5.6 — randomness comes from (1) per-dimension pick, (2) wildcard pick,
//   (3) order jitter (skill always first).
//
// The sampler is **stateless and deterministic** given a seed. The reducer
// in App.tsx re-derives the sequence after every ANSWER. Already-answered
// questions are always preserved at their original positions; only the
// remaining slots get re-planned. This means picking S1 = "cert-pro" causes
// the post-S1 plan to expand into the full AOW-experienced flow.

import type {
  AnswerEntry,
  Dimension,
  Question,
  QuestionId,
} from "./types";

export interface SamplerInput {
  pool: Question[];
  answers: AnswerEntry[];
  rng: () => number;
}

// Slots are the abstract intent at each step, not a 1:1 dimension map.
// SKILL is split into two slots:
//   - SKILL_CERT: always S1 (the hard-gate question; PRD §5.5).
//   - SKILL_CONFIDENCE: optional, random from {S2..S5}; only fires for
//     certified users so we can boost their effective skill silently.
type Slot = Dimension | "WILDCARD" | "SKILL_CERT" | "SKILL_CONFIDENCE";

type Cert = "cert-veteran" | "cert-pro" | "cert-ow" | "cert-tried" | "cert-none" | null;

interface QuickProfile {
  cert: Cert;
  vibe: string | null;
  days: string | null;
}

export function deriveSequence(input: SamplerInput): Question[] {
  const { pool, answers, rng } = input;

  const askedQs = answers
    .map((a) => pool.find((q) => q.id === a.questionId))
    .filter((q): q is Question => q !== undefined);

  if (askedQs.length === 0) {
    const first = pickSkillCert(pool);
    return first ? [first] : [];
  }

  const profile = quickProfile(answers);
  const slots = planSlots(profile);

  // Index already-asked questions for fast reuse.
  const askedByDim = new Map<Dimension, Question[]>();
  const askedWildcards: Question[] = [];
  for (const q of askedQs) {
    if (q.is_wildcard) askedWildcards.push(q);
    else {
      const arr = askedByDim.get(q.dimension) ?? [];
      arr.push(q);
      askedByDim.set(q.dimension, arr);
    }
  }

  const result: Question[] = [];
  const used = new Set<QuestionId>();
  let wildcardCursor = 0;

  for (const slot of slots) {
    if (slot === "WILDCARD") {
      const reuse = askedWildcards[wildcardCursor];
      if (reuse && !used.has(reuse.id)) {
        result.push(reuse);
        used.add(reuse.id);
        wildcardCursor++;
        continue;
      }
      const fresh = pickFromWildcards(pool, used, rng);
      if (fresh) {
        result.push(fresh);
        used.add(fresh.id);
      }
    } else if (slot === "SKILL_CERT") {
      const cert = pickSkillCert(pool);
      if (cert && !used.has(cert.id)) {
        result.push(cert);
        used.add(cert.id);
      }
    } else if (slot === "SKILL_CONFIDENCE") {
      const askedSkill = (askedByDim.get("DIM_SKILL") ?? []).find(
        (q) => q.id !== "QUESTION_S1" && !used.has(q.id)
      );
      if (askedSkill) {
        result.push(askedSkill);
        used.add(askedSkill.id);
        continue;
      }
      const fresh = pickSkillConfidence(pool, used, rng);
      if (fresh) {
        result.push(fresh);
        used.add(fresh.id);
      }
    } else {
      const askedInDim = askedByDim.get(slot) ?? [];
      const reuse = askedInDim.find((q) => !used.has(q.id));
      if (reuse) {
        result.push(reuse);
        used.add(reuse.id);
        continue;
      }
      const fresh = pickFromDim(pool, slot, used, rng);
      if (fresh) {
        result.push(fresh);
        used.add(fresh.id);
      }
    }
  }

  // Preserve any answered question that no longer fits the plan (e.g.,
  // culture answered before user said days = 3-5). Append at the end so
  // they don't get lost; the user has already invested in answering them.
  for (const q of askedQs) {
    if (!used.has(q.id)) {
      result.push(q);
      used.add(q.id);
    }
  }

  return result;
}

// Stable RNG seeded by string (sessionId). FNV-1a → LCG; fast and good enough
// for shuffling pools. Same seed → same sequence, so re-deriving is safe.
export function seededRng(seed: string): () => number {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619) >>> 0;
  }
  return () => {
    h = (Math.imul(h, 1664525) + 1013904223) >>> 0;
    return (h & 0x7fffffff) / 0x7fffffff;
  };
}

// =============================================================
// Profile + plan
// =============================================================

function quickProfile(answers: AnswerEntry[]): QuickProfile {
  const p: QuickProfile = { cert: null, vibe: null, days: null };
  for (const a of answers) {
    if (a.questionId === "QUESTION_S1") p.cert = a.chosenValue as Cert;
    else if (
      a.questionId === "QUESTION_V1" ||
      a.questionId === "QUESTION_V2" ||
      a.questionId === "QUESTION_V3"
    ) {
      p.vibe = a.chosenValue;
    } else if (a.questionId === "QUESTION_D1" || a.questionId === "QUESTION_D2") {
      p.days = a.chosenValue;
    }
  }
  return p;
}

function planSlots(profile: QuickProfile): Slot[] {
  const { cert, vibe, days } = profile;

  // Until we know the cert, just plan the first skill question.
  if (cert === null) return ["SKILL_CERT"];

  const isNonDiver = cert === "cert-tried" || cert === "cert-none";
  const isCertified = cert === "cert-ow" || cert === "cert-pro" || cert === "cert-veteran";
  const isAOWExp = cert === "cert-pro" || cert === "cert-veteran";
  const isShortTrip = days === "3-5";

  const slots: Slot[] = [];

  // Required dimensions (PRD §5.1)
  slots.push("SKILL_CERT", "DIM_VIBE", "DIM_WARMTH", "DIM_BUDGET", "DIM_DAYS");

  // Optional skill-confidence: only for certified users — they're the ones
  // whose effective skill can be silently bumped by a right answer (PRD §5.5).
  if (isCertified) slots.push("SKILL_CONFIDENCE");

  // First-dive concerns is for non-divers only — feels relevant early.
  if (isNonDiver) slots.push("DIM_FIRST_DIVE_CONCERNS");

  // Language is asked unless China-only (we're outbound-only, so for
  // certified divers always; non-divers we can skip — they'll be routed to
  // Chinese-friendly destinations regardless).
  if (isCertified) slots.push("DIM_LANGUAGE");

  // Culture / land vibe — skip on short trips per PRD §5.1.
  if (!isShortTrip) slots.push("DIM_CULTURE");

  // Current tolerance — only meaningful if certified.
  if (isCertified) slots.push("DIM_CURRENT_TOLERANCE");

  // Marine-life specificity — only if certified AND vibe = big-animals.
  if (isCertified && vibe === "bigfish") slots.push("DIM_MARINE_LIFE_SPECIFICITY");

  // Liveaboard tolerance — AOW+ only.
  if (isAOWExp) slots.push("DIM_LIVEABOARD_TOLERANCE");

  // Photography mode — AOW+ only.
  if (isAOWExp) slots.push("DIM_PHOTOGRAPHY_MODE");

  // First wildcard.
  slots.push("WILDCARD");

  // Been-there exclusions — at the end, AOW-experienced only.
  if (isAOWExp) slots.push("DIM_BEEN_THERE");

  // Second wildcard for longer flows.
  if (isAOWExp) slots.push("WILDCARD");

  return slots;
}

// =============================================================
// Pool helpers
// =============================================================

function isActive(q: Question, today = new Date()): boolean {
  if (q.deprecated_at && new Date(q.deprecated_at) <= today) return false;
  if (q.valid_from && new Date(q.valid_from) > today) return false;
  return true;
}

function pickFromDim(
  pool: Question[],
  dim: Dimension,
  exclude: Set<QuestionId>,
  rng: () => number
): Question | undefined {
  const candidates = pool.filter(
    (q) => q.dimension === dim && !q.is_wildcard && !exclude.has(q.id) && isActive(q)
  );
  if (candidates.length === 0) return undefined;
  return candidates[Math.floor(rng() * candidates.length)];
}

function pickFromWildcards(
  pool: Question[],
  exclude: Set<QuestionId>,
  rng: () => number
): Question | undefined {
  const candidates = pool.filter(
    (q) => q.is_wildcard && !exclude.has(q.id) && isActive(q)
  );
  if (candidates.length === 0) return undefined;
  return candidates[Math.floor(rng() * candidates.length)];
}

function pickSkillCert(pool: Question[]): Question | undefined {
  return pool.find((q) => q.id === "QUESTION_S1" && isActive(q));
}

function pickSkillConfidence(
  pool: Question[],
  exclude: Set<QuestionId>,
  rng: () => number
): Question | undefined {
  const candidates = pool.filter(
    (q) =>
      q.dimension === "DIM_SKILL" &&
      q.id !== "QUESTION_S1" &&
      !q.is_wildcard &&
      !exclude.has(q.id) &&
      isActive(q)
  );
  if (candidates.length === 0) return undefined;
  return candidates[Math.floor(rng() * candidates.length)];
}
